import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Edit2 } from "iconsax-react";
import cn from "classnames";
import { Button, DatePicker, Input, Select, ModalForm, Skeleton, Toast, Prompt } from "@/shared/ui";
import { formatDateToISO, extractErrorMessage, type Locale } from "@/shared/utils";
import { useScrollDetection } from "@/shared/hooks";
import { useUpdateSelfAmendmentMutation } from "@/features/self/shared";
import { useGetAmendmentDetailQuery } from "@/features/hr/amendments";
import type { AmendmentDetailResponse, PositionValuesJson, SalaryValuesJson, ScheduleValuesJson, UpdateAmendmentDto } from "@/features/hr/amendments/types";
import type { SelfServiceRequest } from "../types";

interface Props {
  request: SelfServiceRequest;
  onClose: () => void;
  onSuccess: () => void;
}

function parseDateString(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null;
  const dayOnly = String(dateStr).split("T")[0];
  const date = new Date(dayOnly + "T00:00:00");
  return isNaN(date.getTime()) ? null : date;
}

function isPositionValues(v: unknown): v is PositionValuesJson {
  return typeof v === "object" && v !== null && ("job_position_ru" in v || "job_position_kk" in v);
}

function isSalaryValues(v: unknown): v is SalaryValuesJson {
  return typeof v === "object" && v !== null && "salary_amount" in v;
}

function isScheduleValues(v: unknown): v is ScheduleValuesJson {
  return typeof v === "object" && v !== null && "work_start_time" in v;
}

function EditSelfAmendmentFormSkeleton() {
  return (
    <div className="flex flex-col h-full gap-5 py-5">
      <Skeleton height={28} borderRadius={8} />
      <Skeleton height={16} width={200} borderRadius={8} />
      <Skeleton height={42} borderRadius={12} />
      <Skeleton height={42} borderRadius={12} />
      <Skeleton height={42} borderRadius={12} />
      <Skeleton height={42} borderRadius={12} />
    </div>
  );
}

export default function EditSelfAmendmentForm({ request, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";

  const { data: amendment, isLoading: isLoadingDetail, isError } = useGetAmendmentDetailQuery(request.id);
  const { scrollRef, hasScroll } = useScrollDetection();
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [updateAmendment, { isLoading: isSubmitting }] = useUpdateSelfAmendmentMutation();

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSuccess();
    }
  }

  return (
    <>
      {prompt && (
        <Prompt
          variant={prompt.variant}
          onClose={handlePromptClose}
          title={prompt.title}
          text={prompt.text}
          namespace="EmployeePortal"
        />
      )}

      {isLoadingDetail || isError || !amendment ? (
        <ModalForm icon={Edit2} onClose={onClose}>
          {isLoadingDetail ? (
            <EditSelfAmendmentFormSkeleton />
          ) : (
            <div className="flex flex-col gap-6 h-full min-h-0">
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <h4 className="text-display-2xs content-base-primary">{t("requests.amendment.editTitle")}</h4>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <p className="text-body-regular-md content-action-negative">{t("requests.amendment.editErrorText")}</p>
              </div>
            </div>
          )}
        </ModalForm>
      ) : (
        <EditAmendmentFormInner
          amendment={amendment}
          reviewNote={request.application_review_note}
          onClose={onClose}
          onSuccess={onSuccess}
          scrollRef={scrollRef}
          hasScroll={hasScroll}
          prompt={prompt}
          setPrompt={setPrompt}
          updateAmendment={updateAmendment}
          isSubmitting={isSubmitting}
          t={t}
          locale={locale}
        />
      )}
    </>
  );
}

interface InnerProps {
  amendment: AmendmentDetailResponse;
  reviewNote: string | null;
  onClose: () => void;
  onSuccess: () => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  hasScroll: boolean;
  prompt: { title: string; text: string; variant: "success" | "error" } | null;
  setPrompt: (p: { title: string; text: string; variant: "success" | "error" } | null) => void;
  updateAmendment: ReturnType<typeof useUpdateSelfAmendmentMutation>[0];
  isSubmitting: boolean;
  t: (key: string) => string;
  locale: Locale;
}

function EditAmendmentFormInner({
  amendment,
  reviewNote,
  onClose,
  scrollRef,
  hasScroll,
  setPrompt,
  updateAmendment,
  isSubmitting,
  t,
  locale,
}: InnerProps) {
  const newValues = amendment.new_values_json;
  const clauseSection = amendment.clause.section_number;

  const [effectiveDate, setEffectiveDate] = useState<Date | null>(
    parseDateString(amendment.effective_date),
  );

  const posVals = isPositionValues(newValues) ? newValues : null;
  const [jobPositionRu, setJobPositionRu] = useState(posVals?.job_position_ru || "");
  const [jobPositionKk, setJobPositionKk] = useState(posVals?.job_position_kk || "");
  const [jobDutiesRu, setJobDutiesRu] = useState(posVals?.job_duties_ru?.join("\n") || "");
  const [jobDutiesKk, setJobDutiesKk] = useState(posVals?.job_duties_kk?.join("\n") || "");

  const salVals = isSalaryValues(newValues) ? newValues : null;
  const [salaryAmount, setSalaryAmount] = useState(salVals?.salary_amount || "");

  const schVals = isScheduleValues(newValues) ? newValues : null;
  const [workStartTime, setWorkStartTime] = useState(schVals?.work_start_time || "");
  const [workEndTime, setWorkEndTime] = useState(schVals?.work_end_time || "");
  const [breakStartTime, setBreakStartTime] = useState(schVals?.break_start_time || "");
  const [breakEndTime, setBreakEndTime] = useState(schVals?.break_end_time || "");
  const [workingDays, setWorkingDays] = useState(schVals?.working_days_list?.join(", ") || "");

  const [clauseTextRu, setClauseTextRu] = useState(amendment.new_clause_text_ru || "");
  const [clauseTextKk, setClauseTextKk] = useState(amendment.new_clause_text_kk || "");

  const isStructured = clauseSection === "1.1" || clauseSection === "4.12" || clauseSection === "4.1";

  function buildPayload(): UpdateAmendmentDto {
    const effective_date = effectiveDate ? formatDateToISO(effectiveDate) : "";

    if (clauseSection === "1.1") {
      const vals: PositionValuesJson = {
        job_position_ru: jobPositionRu,
        job_position_kk: jobPositionKk,
        job_duties_ru: jobDutiesRu.split("\n").filter(Boolean),
        job_duties_kk: jobDutiesKk.split("\n").filter(Boolean),
      };
      return { effective_date, new_values: vals };
    }

    if (clauseSection === "4.12") {
      const vals: SalaryValuesJson = { salary_amount: salaryAmount };
      return { effective_date, new_values: vals };
    }

    if (clauseSection === "4.1") {
      const vals: ScheduleValuesJson = {
        work_start_time: workStartTime,
        work_end_time: workEndTime,
        break_start_time: breakStartTime,
        break_end_time: breakEndTime,
        working_days_list: workingDays.split(",").map((d) => d.trim()).filter((d) => d !== "" && !isNaN(Number(d))),
      };
      return { effective_date, new_values: vals };
    }

    return {
      effective_date,
      new_clause_text_ru: clauseTextRu,
      new_clause_text_kk: clauseTextKk,
    };
  }

  async function handleSubmitForm(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !effectiveDate) return;

    try {
      const payload = buildPayload();
      await updateAmendment({ id: amendment.id, data: payload }).unwrap();
      setPrompt({
        title: t("requests.amendment.editSuccessTitle"),
        text: t("requests.amendment.editSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("requests.amendment.editErrorTitle"),
        text: extractErrorMessage(err, "requests.amendment.editErrorText", t as never),
        variant: "error",
      });
    }
  }

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <ModalForm
      icon={Edit2}
      onClose={onClose}
      footer={
        <div className="grid grid-cols-[2fr_3fr] gap-3">
          <Button variant="secondary" size="huge" onClick={onClose} type="button">
            {t("leaveForm.cancel")}
          </Button>
          <Button variant="primary" size="huge" type="submit" form="edit-self-amendment-form" disabled={isSubmitting || !effectiveDate}>
            {isSubmitting ? t("leaveForm.saving") : t("leaveForm.save")}
          </Button>
        </div>
      }
    >
      <form id="edit-self-amendment-form" className="flex flex-col h-full" onSubmit={handleSubmitForm}>
        <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("requests.amendment.editTitle")}</h4>
          <p className="text-body-regular-sm content-base-secondary">{t("requests.amendment.editSubtitle")}</p>
        </div>

        <div ref={scrollRef} className={cn("flex-1 overflow-auto flex flex-col gap-4 py-5 px-1 page-scroll", hasScroll && "pr-3")}>
          {reviewNote && (
            <Toast
              color="notice"
              text={`${t("leaveForm.revisionNotePrefix")}: ${reviewNote}`}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}

          <DatePicker
            label={t("requests.amendment.effectiveDate")}
            placeholder={t("requests.amendment.effectiveDatePlaceholder")}
            value={effectiveDate}
            onChange={setEffectiveDate}
            locale={locale}
            minDate={tomorrow}
          />

          {clauseSection === "1.1" && renderPositionFields()}
          {clauseSection === "4.12" && renderSalaryFields()}
          {clauseSection === "4.1" && renderScheduleFields()}
          {!isStructured && renderClauseFields()}
        </div>

      </form>
    </ModalForm>
  );

  function renderPositionFields() {
    return (
      <>
        <Input
          label={t("requests.amendment.jobPositionRu")}
          placeholder={t("requests.amendment.jobPositionRuPlaceholder")}
          value={jobPositionRu}
          onChange={(e) => setJobPositionRu(e.target.value)}
        />
        <Input
          label={t("requests.amendment.jobPositionKk")}
          placeholder={t("requests.amendment.jobPositionKkPlaceholder")}
          value={jobPositionKk}
          onChange={(e) => setJobPositionKk(e.target.value)}
        />
        <Input
          label={t("requests.amendment.jobDutiesRu")}
          placeholder={t("requests.amendment.jobDutiesRuPlaceholder")}
          isTextarea
          value={jobDutiesRu}
          onChange={(e) => setJobDutiesRu(e.target.value)}
        />
        <Input
          label={t("requests.amendment.jobDutiesKk")}
          placeholder={t("requests.amendment.jobDutiesKkPlaceholder")}
          isTextarea
          value={jobDutiesKk}
          onChange={(e) => setJobDutiesKk(e.target.value)}
        />
      </>
    );
  }

  function renderSalaryFields() {
    return (
      <Input
        label={t("requests.amendment.salaryAmount")}
        placeholder={t("requests.amendment.salaryAmountPlaceholder")}
        value={salaryAmount}
        onChange={(e) => setSalaryAmount(e.target.value)}
      />
    );
  }

  function renderScheduleFields() {
    return (
      <>
        <Input
          label={t("requests.amendment.workStartTime")}
          placeholder="09:00"
          value={workStartTime}
          onChange={(e) => setWorkStartTime(e.target.value)}
        />
        <Input
          label={t("requests.amendment.workEndTime")}
          placeholder="18:00"
          value={workEndTime}
          onChange={(e) => setWorkEndTime(e.target.value)}
        />
        <Input
          label={t("requests.amendment.breakStartTime")}
          placeholder="13:00"
          value={breakStartTime}
          onChange={(e) => setBreakStartTime(e.target.value)}
        />
        <Input
          label={t("requests.amendment.breakEndTime")}
          placeholder="14:00"
          value={breakEndTime}
          onChange={(e) => setBreakEndTime(e.target.value)}
        />
        <Input
          label={t("requests.amendment.workingDays")}
          placeholder={t("requests.amendment.workingDaysPlaceholder")}
          value={workingDays}
          onChange={(e) => setWorkingDays(e.target.value)}
        />
      </>
    );
  }

  function renderClauseFields() {
    return (
      <>
        <Input
          label={t("requests.amendment.clauseTextRu")}
          placeholder={t("requests.amendment.clauseTextRuPlaceholder")}
          isTextarea
          value={clauseTextRu}
          onChange={(e) => setClauseTextRu(e.target.value)}
        />
        <Input
          label={t("requests.amendment.clauseTextKk")}
          placeholder={t("requests.amendment.clauseTextKkPlaceholder")}
          isTextarea
          value={clauseTextKk}
          onChange={(e) => setClauseTextKk(e.target.value)}
        />
      </>
    );
  }
}
